"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

const authPageLayoutVariants = cva(
  "relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-6 sm:py-8",
  {
    variants: {
      scenery: {
        none: "bg-background",
        aurora: "bg-white dark:bg-zinc-950",
      },
    },
    defaultVariants: { scenery: "none" },
  }
);

export interface AuthPageLayoutProps extends VariantProps<typeof authPageLayoutVariants> {
  children: React.ReactNode;
  controls?: React.ReactNode;
  showAuroraEffects?: boolean;
}

export function AuthPageLayout({
  children,
  controls,
  scenery = "none",
  showAuroraEffects = true,
}: AuthPageLayoutProps) {
  return (
    <div className={authPageLayoutVariants({ scenery })}>
      {scenery === "aurora" && showAuroraEffects && (
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-[-20%] h-[560px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(167,139,250,0.18)_0%,rgba(139,92,246,0.06)_55%,transparent_75%)] blur-3xl" />
          <div className="absolute -bottom-[12%] -left-[10%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.12)_0%,rgba(6,182,212,0.04)_55%,transparent_75%)] blur-3xl" />
          <div className="absolute -right-[10%] bottom-[4%] h-[360px] w-[360px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(244,114,182,0.11)_0%,rgba(236,72,153,0.03)_55%,transparent_75%)] blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgb(148_163_184/0.18)_1px,transparent_0)] bg-[length:24px_24px] opacity-35 dark:opacity-[0.06]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/0 to-white/30 dark:to-zinc-950/35" />
        </div>
      )}

      {controls && (
        <div className="absolute right-4 top-4 z-20 flex items-center gap-2 sm:right-6 sm:top-6">
          {controls}
        </div>
      )}

      <div className="relative z-10 flex w-full justify-center overflow-visible">
        {children}
      </div>
    </div>
  );
}
